/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from '@mui/material';
import { Call, CallDirection, CallDisconnectStatus, CallStatus, CallWithTimestamp } from './typings';
import { getCurrentUserId } from '../../infra/firebase/init';
import CallQueueBanner from './CallQueueBanner';
import { getCallsUpdateApi, getNewReconnectSDPApi } from './request';
import useInfinitePolling from '../../shared/hooks/use-infinite-poll';
import { useUserPermissions } from '../../shared/hooks/use-permission';
import { useAtom } from 'jotai';
import { callsAtom } from './states';
import useWabaLevelPermission from '../../shared/hooks/use-waba-level-permission';
import { Permissions } from '../../shared/typings/user';
import { CALL_UPDATES_POLL_MAX_RETRIES, CALL_UPDATES_POLL_RETRY_TIME_MS, CALL_UPDATES_POLL_TIME_MS, CALL_EXPIRY_DURATION_MS } from './constants';

const CALL_CLEANUP_DELAY = 3000; // 4 seconds

const getCallStartTime = (callStartedAt: string | null) => {
  if (!callStartedAt) return undefined;
  const callStartedAtDate = new Date(callStartedAt);
  return callStartedAtDate.getTime();
};

const CallManager: React.FC = () => {
  const [calls, setCalls] = useAtom(callsAtom);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingCall, setPendingCall] = useState<Call | null>(null);
  const currentUserId = getCurrentUserId();
  const { hasPermission, getWabasWithPermission } = useWabaLevelPermission();


  const pushNewCall = (newCall: CallWithTimestamp, makeActive?: boolean) => {
    // prevent duplicates
    const existingCallIndex = calls.findIndex((call) => call.callId === newCall.callId);
    if (existingCallIndex !== -1) return;
    setCalls((prev) => {
      if (prev.length === 0 || makeActive) {
        setActiveCallId(newCall.callId);
      }
      return [...prev, newCall];
    });
  };

  const removeCallById = (callId: string) => {
    setCalls((prev) => prev.filter((call) => call.callId !== callId));
    setActiveCallId((prev) => (prev === callId ? null : prev));
  };

  // Cleanup disconnected calls after delay
  const cleanupCall = useCallback((callId: string) => {
    setTimeout(() => {
      removeCallById(callId);
    }, CALL_CLEANUP_DELAY);
  }, []);

  const getCallsUpdate = useMemo(() => {
    return async () => {
      try {
        const result = await getCallsUpdateApi();
        return result;
      } catch (error) {
        console.error('Failed to get calls update:', error);

        // Don't throw the error to prevent infinite retries
        // The useInfinitePolling hook will handle retries based on its configuration
        return { activeCalls: [] };
      }
    };
  }, []);

  const wabasWithReceiveCallPermission = useMemo(() => {
    return getWabasWithPermission(Permissions.RECEIVE_CALL);
  }, [getWabasWithPermission]);

  const { data } = useInfinitePolling(getCallsUpdate, {
    interval: CALL_UPDATES_POLL_TIME_MS,
    maxRetries: CALL_UPDATES_POLL_MAX_RETRIES,
    retryDelay: CALL_UPDATES_POLL_RETRY_TIME_MS,
    enabled: wabasWithReceiveCallPermission.wabas.length > 0,
    dependencies: [],
  });

  const getNewReconnectSDP = async (callId: string, callOriginType: CallDirection) => {
    const { data: newSDP } = await getNewReconnectSDPApi({
      callId,
      callOriginType,
    });
    return newSDP;
  };
  
  useEffect(() => {
    setCalls((prevCalls) => {
      let updatedCalls = [...prevCalls];

      data?.activeCalls.forEach(async (apiCall) => {
        try {
          if (!apiCall.callId || !apiCall.metadata?.offerForCallee) {
            console.error('Invalid call data received:', apiCall);
            return;
          }
          if(!hasPermission(apiCall.integrationId, Permissions.RECEIVE_CALL).hasAccess) {
            return;
          }
          const existingCallIndex = updatedCalls.findIndex((call) => call.callId === apiCall.callId);

          if (existingCallIndex === -1) {
            const newSDP =
              apiCall.status === CallStatus.IN_PROGRESS
                ? await getNewReconnectSDP(apiCall.callId, CallDirection.INCOMING)
                : undefined;

            const newCall: CallWithTimestamp = {
              callId: apiCall.callId,
              customerName: apiCall.customerName,
              customerNumber: apiCall.customerNumber,
              wabaNumber: apiCall.wabaNumber,
              wabaName: apiCall.wabaName,
              status: apiCall.status === CallStatus.IN_PROGRESS ? CallStatus.INITIATED : apiCall.status, // INITIATED is for reconnect case
              sdp: newSDP ? newSDP.sdp : apiCall.metadata.offerForCallee.sdp,
              sdpType: newSDP ? newSDP.type : apiCall.metadata.offerForCallee.type,
              callStartTime:
                apiCall.status === CallStatus.IN_PROGRESS ? getCallStartTime(apiCall.callStartedAt) : undefined,
              // Important: we need to set shouldAnswerCall to true for reconnect case
              shouldAnswerCall: apiCall.status === CallStatus.IN_PROGRESS,
              reconnect: apiCall.status === CallStatus.IN_PROGRESS,
              dateCreated: apiCall.dateCreated,
              // new date time stamp 60 sec more than dateCreated
              expiryTimestamp: apiCall.dateCreated ? new Date(apiCall.dateCreated).getTime() + CALL_EXPIRY_DURATION_MS : undefined,
            };
            updatedCalls.push(newCall);
            if (prevCalls.length === 0 || apiCall.status === CallStatus.IN_PROGRESS) {
              setActiveCallId(apiCall.callId);
            }
          }
        } catch (error) {
          console.error('Error processing call update:', error, apiCall);
        }
      });

      // rmv calls which are no more in updates api
      updatedCalls = updatedCalls.filter((call) =>  {
        if(data?.activeCalls.some((apiCall) => apiCall.callId === call.callId)) return true;
        // check for expired call (initiated more than 60 sec ago)
        if(call.status === CallStatus.INITIATED && call.expiryTimestamp && +call.expiryTimestamp < Date.now()) return false;
        // if call is not in api call and was started more 12 seconds ago, remove it (not present event in 2 updates api calls)
        if(call.dateCreated && new Date(call.dateCreated).getTime() + (2*CALL_UPDATES_POLL_TIME_MS) > Date.now()) return true;
        return false;
      });

      return updatedCalls;
    });
  }, [data]);

  useEffect(() => {
    const handleSocketEvents = (event: MessageEvent) => {
      // if (!hasPermission(event.data.integrationId, Permissions.RECEIVE_CALL).hasAccess) return;
      switch (event.data.type) {
        case 'INCOMING_CALL':
          const newCall: CallWithTimestamp = {
            ...event.data.payload,
            status: CallStatus.INITIATED,
          };
          pushNewCall(newCall);
          break;

        case 'CALL_PICKED':
          const { userId, callId } = event.data.payload;
          if (userId === currentUserId) {
            const call = calls.find((c) => c.callId === callId);
            if (call) {
              // If call is not in connected/connecting state, remove it (call picked on another tab)
              if (call.status !== CallStatus.IN_PROGRESS && call.status != CallStatus.Connecting) {
                removeCallById(callId);
              }
            }
          } else {
            // Someone else picked the call, remove it from our screen
            removeCallById(callId);
          }
          break;

        default:
          break;
      }
    };

    window.addEventListener('message', handleSocketEvents);
    return () => window.removeEventListener('message', handleSocketEvents);
  }, [cleanupCall, currentUserId, calls]);

  const handleAcceptCall = (incomingCall: Call) => {
    const activeCall = calls.find(
      (call) => call.status === CallStatus.IN_PROGRESS || call.status === CallStatus.Connecting
    );

    if (activeCall) {
      setPendingCall(incomingCall);
      setShowConfirmation(true);
    } else {
      answerCall(incomingCall);
    }
  };

  const answerCall = (call: Call) => {
    setCalls((prev) =>
      prev.map((c) => ({
        ...c,
        shouldAnswerCall: c.callId === call.callId ? true : c.shouldAnswerCall,
      }))
    );
    setActiveCallId(call.callId);
  };

  const onAcceptStateHandler = (call: Call, state: CallStatus) => {
    setCalls((prev) =>
      prev.map((c) => ({
        ...c,
        status: c.callId === call.callId ? state : c.status,
        // Set call start time when call becomes connected
        callStartTime:
          c.callId === call.callId && state === CallStatus.IN_PROGRESS && !c.callStartTime
            ? Date.now()
            : c.callStartTime,
      }))
    );
  };

  const handleDisconnect = (status: CallDisconnectStatus, disconnectedCall: Call) => {
    // Mark as disconnected first, then cleanup after delay
    setCalls((prev) => prev.map((call) => (call.callId === disconnectedCall.callId ? { ...call, status } : call)));
    cleanupCall(disconnectedCall.callId);
  };

  const handleSwitchCall = () => {
    if (pendingCall) {
      const activeCall = calls.find(
        (call) => call.status === CallStatus.IN_PROGRESS || call.status === CallStatus.Connecting
      );
      if (activeCall) {
        // Trigger force disconnect
        setCalls((prev) =>
          prev.map((c) => ({
            ...c,
            shouldForceDisconnect: c.callId === activeCall.callId ? true : c.shouldForceDisconnect,
          }))
        );
        cleanupCall(activeCall.callId);
      }
      answerCall(pendingCall);

      setPendingCall(null);
      setShowConfirmation(false);
    }
  };

  const handleCallClick = (call: Call) => {
    setActiveCallId(call.callId);
  };

  const handleCallMinimize = (call: Call) => {
    if (activeCallId === call.callId) {
      setActiveCallId(null);
    }
  };

  const handleCallClose = (call: Call) => {
    setCalls((prev) => prev.filter((c) => c.callId !== call.callId));

    // If the closed call was active, set the next call as active (if any)
    if (activeCallId === call.callId) {
      const remainingCalls = calls.filter((c) => c.callId !== call.callId);
      setActiveCallId(remainingCalls.length > 0 ? remainingCalls[0].callId : null);
    }
  };

  const orderedCalls = useMemo(() => {
    if (!activeCallId) return calls;
    const active = calls.find((c) => c.callId === activeCallId);
    const others = calls.filter((c) => c.callId !== activeCallId);
    return active ? [active, ...others] : calls;
  }, [calls, activeCallId]);

  return (
    <>
      <CallQueueBanner
        calls={orderedCalls}
        activeCallId={activeCallId}
        onSelect={handleCallClick}
        onMinimize={handleCallMinimize}
        onCloseChip={handleCallClose}
        onDisconnect={handleDisconnect}
        onAcceptStateChange={(call, state) => onAcceptStateHandler(call, state)}
        onRequestAccept={handleAcceptCall}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={() => {
          setShowConfirmation(false);
          setPendingCall(null);
        }}
      >
        <DialogTitle>Switch Call?</DialogTitle>
        <DialogContent>
          <Typography>
            You're already on a call. Accepting this call from{' '}
            {pendingCall?.customerName ?? pendingCall?.customerNumber} will end your current call. Do you want to
            switch?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowConfirmation(false);
              setPendingCall(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleSwitchCall} color="error" variant="contained">
            End Current & Switch
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CallManager;
